import React from 'react';

/**
 * Biometric sensor glyphs.
 *
 * ON "OFFICIAL" ARTWORK — the honest position, because it decides what we can
 * ship rather than what looks nicest (raised by Jason 2026-08-19):
 *
 * Apple's `faceid` / `touchid` SF Symbols are licensed for use *in apps on
 * Apple platforms*, and Apple's HIG asks that Face ID and Touch ID be referred
 * to with them. But these render inside a WebView that also serves Android and
 * the browser, and the SF Symbols license does not cover redistributing the
 * artwork as SVG to non-Apple platforms. Shipping Apple's file here would put
 * it on Android too.
 *
 * So these are original paths drawn to Apple's PUBLISHED geometry — the same
 * construction every player recognises from the system prompt: four corner
 * brackets on a 24-unit grid with a 2-unit corner radius, eyes at ±3.5 from
 * centre, a nose that hooks left, and a shallow smile. Apple's SPECIFICATION
 * is a fact about the platform; only their file is licensed.
 *
 * SETTLED (owner 2026-08-20): these ship as they are. Loading Apple's real
 * symbol on iOS and these elsewhere was considered and dropped — it buys a
 * difference nobody can see at 20px in exchange for a platform-gated asset
 * pipeline. No Legal question remains open on this ticket.
 *
 * Craft notes:
 *   - Stroke is 1.5 at the 20px base, matching the optical weight of the d4
 *     body text beside it (better-ui: icon stroke tracks text weight). Scaled
 *     sizes keep that ratio via `strokeWidth = 1.5 * (24 / size) * (size / 20)`
 *     collapsing to a constant in user units — i.e. the stroke thickens with
 *     the glyph rather than staying hairline at 44px.
 *   - `strokeLinecap: round` throughout: Apple's symbols are round-capped, and
 *     butt caps read as clinical at these sizes.
 *   - Everything is `currentColor`, so state comes from CSS, never a second
 *     asset.
 */

export type BiometryKind = 'faceid' | 'touchid' | 'fingerprint';

/** Player-facing sensor name. DRAFT copy pending Legal. */
export const biometryName = (kind: BiometryKind): string =>
  kind === 'faceid' ? 'Face ID' : kind === 'touchid' ? 'Touch ID' : 'fingerprint';

/**
 * Title-cased variant for headings/buttons where "fingerprint" leads a label
 * ("Use Fingerprint"). Face ID / Touch ID are trademarks and never re-case.
 */
export const biometryTitle = (kind: BiometryKind): string =>
  kind === 'fingerprint' ? 'Fingerprint' : biometryName(kind);

export function SensorGlyph({ kind, size = 20 }: { kind: BiometryKind; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
    style: { flex: 'none', display: 'block' }
  };

  if (kind === 'faceid') {
    // Corner brackets (2-unit radius, 4.5-unit arms) + face, on Apple's grid.
    return (
      <svg {...common}>
        <path d='M3.25 8.25V5.75A2.5 2.5 0 0 1 5.75 3.25h2.5' />
        <path d='M15.75 3.25h2.5a2.5 2.5 0 0 1 2.5 2.5v2.5' />
        <path d='M20.75 15.75v2.5a2.5 2.5 0 0 1-2.5 2.5h-2.5' />
        <path d='M8.25 20.75h-2.5a2.5 2.5 0 0 1-2.5-2.5v-2.5' />
        {/* eyes: short verticals at ±3.5 from centre */}
        <path d='M8.5 9.25v1.75' />
        <path d='M15.5 9.25v1.75' />
        {/* nose: descends then hooks left, as Apple draws it */}
        <path d='M12 9.25v4a1 1 0 0 1-1 1h-.35' />
        {/* mouth: shallow, symmetric */}
        <path d='M8.6 16.4c.95.78 2.09 1.17 3.4 1.17s2.45-.39 3.4-1.17' />
      </svg>
    );
  }

  // Touch ID / Android fingerprint: concentric whorl. Arcs are open at
  // alternating ends so it reads as a fingerprint rather than a target.
  return (
    <svg {...common}>
      <path d='M12 3.9a8.1 8.1 0 0 0-8.1 8.1c0 2.05.4 3.9 1.2 5.6' />
      <path d='M20.1 12A8.1 8.1 0 0 0 12 3.9' />
      <path d='M12 7.4A4.6 4.6 0 0 0 7.4 12c0 2.6.7 5 2 7.1' />
      <path d='M16.6 12A4.6 4.6 0 0 0 12 7.4' />
      <path d='M12 10.9v1.1c0 3.1 1 6 2.8 8.4' />
      <path d='M17.9 15.4a13 13 0 0 1-.6 2.9' />
    </svg>
  );
}

/**
 * The sensor inside the d4 icon disc. A distinct component (rather than
 * extending d4's DiscIcon union) because the prototype must stay additive —
 * zero edits to the registration family's files.
 */
export function SensorDisc({
  kind,
  disc,
  size = 54
}: {
  kind: BiometryKind;
  disc: { bg: string; border: string; stroke: string };
  size?: number;
}) {
  return (
    <div
      aria-hidden='true'
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: disc.bg,
        border: disc.border,
        color: disc.stroke,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none'
      }}>
      <SensorGlyph kind={kind} size={Math.round(size * 0.52)} />
    </div>
  );
}
